import { useEffect, useMemo, useRef, useState } from "react";
import { Minus, Plus, Scan } from "lucide-react";

export const DEPT_COLORS = {
  Executive: "var(--brand-primary)", Engineering: "var(--brand-accent)", "Data & AI": "#0EA5E9",
  Sales: "#D97706", Finance: "#059669", HR: "#E11D48", Operations: "#64748B",
};

const NODE_W = 176, NODE_H = 60, XGAP = 18, YGAP = 48;

function computeLayout(employees, collapsed) {
  const byId = new Map(employees.map((e) => [e.id, e]));
  const children = new Map();
  const roots = [];
  employees.forEach((e) => {
    if (e.manager_id && byId.has(e.manager_id)) {
      if (!children.has(e.manager_id)) children.set(e.manager_id, []);
      children.get(e.manager_id).push(e.id);
    } else roots.push(e.id);
  });
  children.forEach((kids) => kids.sort((a, b) => {
    const ea = byId.get(a), eb = byId.get(b);
    return (ea.department || "").localeCompare(eb.department || "") || a.localeCompare(b);
  }));
  const counts = new Map();
  const countDesc = (id) => {
    if (counts.has(id)) return counts.get(id);
    const n = (children.get(id) || []).reduce((acc, c) => acc + 1 + countDesc(c), 0);
    counts.set(id, n);
    return n;
  };
  employees.forEach((e) => countDesc(e.id));

  const pos = new Map();
  let cursor = 0, maxDepth = 0;
  const place = (id, depth) => {
    maxDepth = Math.max(maxDepth, depth);
    const kids = collapsed.has(id) ? [] : children.get(id) || [];
    if (!kids.length) {
      pos.set(id, { x: cursor, y: depth * (NODE_H + YGAP) });
      cursor += NODE_W + XGAP;
      return;
    }
    kids.forEach((k) => place(k, depth + 1));
    const xs = kids.map((k) => pos.get(k).x);
    pos.set(id, { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: depth * (NODE_H + YGAP) });
  };
  roots.forEach((r) => place(r, 0));
  return { pos, children, byId, counts, roots, width: Math.max(cursor - XGAP, NODE_W), height: (maxDepth + 1) * (NODE_H + YGAP) };
}

const densityStyle = (v) => (v >= 70 ? "bg-teal-50 text-teal-800" : v >= 50 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700");
const BAND_STYLE = { low: "bg-emerald-50 text-emerald-700", medium: "bg-amber-50 text-amber-800", high: "bg-red-50 text-red-700" };

function NodeChip({ emp, overlay, od }) {
  if (emp.is_proposed)
    return <span className="text-[8px] font-mono uppercase tracking-wider bg-teal-700 text-white px-1 py-0.5">NEW</span>;
  if (overlay === "density" && od?.skill_density != null)
    return <span className={`text-[9px] font-mono px-1 py-0.5 ${densityStyle(od.skill_density)}`}>{od.skill_density}%</span>;
  if (overlay === "comp" && od?.compensation)
    return (
      <span className="text-[9px] font-mono px-1 py-0.5 bg-slate-100 text-slate-700">
        {od.compensation.band} · {Math.round(od.compensation.total / 1000)}k
      </span>
    );
  if (overlay === "attrition" && od?.attrition)
    return <span className={`text-[9px] font-mono px-1 py-0.5 ${BAND_STYLE[od.attrition.band]}`}>{od.attrition.score}</span>;
  return <span className="text-[9px] font-mono px-1 py-0.5 bg-slate-100 text-slate-500">L{emp.level ?? "—"}</span>;
}

export default function OrgChartCanvas({
  employees, overlay = "reporting", overlayData = {}, selectedId, onSelect,
  highlightIds, height = 600, testId = "org-chart-canvas",
}) {
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const fittedRef = useRef(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [view, setView] = useState({ x: 60, y: 24, k: 0.7 });

  const layout = useMemo(() => computeLayout(employees || [], collapsed), [employees, collapsed]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      setView((v) => {
        const k2 = Math.min(2.5, Math.max(0.12, v.k * Math.exp(-e.deltaY * 0.0012)));
        return { k: k2, x: mx - (mx - v.x) * (k2 / v.k), y: my - (my - v.y) * (k2 / v.k) };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    if (fittedRef.current || !layout.roots.length || !containerRef.current) return;
    fittedRef.current = true;
    const cw = containerRef.current.clientWidth;
    const rootX = layout.pos.get(layout.roots[0])?.x || 0;
    setView({ k: 0.7, x: cw / 2 - (rootX + NODE_W / 2) * 0.7, y: 24 });
  }, [layout]);

  const fit = () => {
    const el = containerRef.current;
    if (!el) return;
    const k = Math.min(1, (el.clientWidth - 48) / layout.width, (height - 48) / layout.height);
    setView({ k, x: (el.clientWidth - layout.width * k) / 2, y: 24 });
  };
  const zoomBy = (f) =>
    setView((v) => {
      const el = containerRef.current;
      const cx = el ? el.clientWidth / 2 : 400, cy = height / 2;
      const k2 = Math.min(2.5, Math.max(0.12, v.k * f));
      return { k: k2, x: cx - (cx - v.x) * (k2 / v.k), y: cy - (cy - v.y) * (k2 / v.k) };
    });

  const onPointerDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: view.x, oy: view.y };
    movedRef.current = false;
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) movedRef.current = true;
    if (movedRef.current) setView((v) => ({ ...v, x: d.ox + dx, y: d.oy + dy }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const edges = [];
  layout.pos.forEach((p, id) => {
    if (collapsed.has(id)) return;
    (layout.children.get(id) || []).forEach((cid) => {
      const c = layout.pos.get(cid);
      if (!c) return;
      const x1 = p.x + NODE_W / 2, y1 = p.y + NODE_H, x2 = c.x + NODE_W / 2, y2 = c.y;
      const midY = y1 + YGAP / 2;
      edges.push(<path key={`${id}-${cid}`} d={`M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`} fill="none" stroke="#CBD5E1" strokeWidth="1" />);
    });
  });

  const dimming = highlightIds && highlightIds.size > 0;

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="relative overflow-hidden bg-white border border-border select-none cursor-grab active:cursor-grabbing"
      style={{ height, backgroundImage: "radial-gradient(#E2E8F0 1px, transparent 1px)", backgroundSize: "22px 22px" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <div style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`, transformOrigin: "0 0", position: "absolute" }}>
        <svg width={layout.width + NODE_W} height={layout.height + NODE_H} style={{ position: "absolute", left: 0, top: 0, overflow: "visible" }}>
          {edges}
        </svg>
        {[...layout.pos.entries()].map(([id, p]) => {
          const emp = layout.byId.get(id);
          const od = overlayData[id];
          const isSel = selectedId === id;
          const isHl = highlightIds?.has(id);
          const kidsTotal = layout.counts.get(id) || 0;
          const hasKids = (layout.children.get(id) || []).length > 0 || (collapsed.has(id) && kidsTotal > 0);
          return (
            <div
              key={id}
              data-testid={`org-node-${id}`}
              onClick={(e) => { e.stopPropagation(); if (!movedRef.current) onSelect?.(emp); }}
              className={`absolute bg-white transition-shadow cursor-pointer ${
                emp.is_proposed ? "border border-dashed border-teal-600" : "border border-slate-200"
              } ${isSel ? "ring-2 ring-teal-600" : ""} ${isHl ? "ring-2 ring-red-500" : ""} ${dimming && !isHl ? "opacity-40" : ""} hover:shadow-md`}
              style={{ left: p.x, top: p.y, width: NODE_W, height: NODE_H, borderLeft: `3px solid ${DEPT_COLORS[emp.department] || "#94A3B8"}` }}
            >
              <div className="px-2 pt-1.5 flex items-start justify-between gap-1">
                <p className="text-[11px] font-semibold text-slate-800 leading-tight truncate">{emp.name}</p>
                <NodeChip emp={emp} overlay={overlay} od={od} />
              </div>
              <p className="px-2 text-[9.5px] text-slate-500 truncate leading-tight">{emp.title}</p>
              {hasKids && (
                <button
                  data-testid={`org-node-toggle-${id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsed((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id); else next.add(id);
                      return next;
                    });
                  }}
                  className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 bg-[var(--brand-primary)] text-white text-[8px] font-mono px-1.5 py-px leading-tight hover:bg-teal-700"
                >
                  {collapsed.has(id) ? `+${kidsTotal}` : "−"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="absolute top-3 right-3 flex flex-col border border-border bg-white divide-y divide-border z-10">
        <button data-testid="org-zoom-in" onClick={() => zoomBy(1.25)} className="p-1.5 hover:bg-slate-50 text-slate-600"><Plus className="h-3.5 w-3.5" /></button>
        <button data-testid="org-zoom-out" onClick={() => zoomBy(0.8)} className="p-1.5 hover:bg-slate-50 text-slate-600"><Minus className="h-3.5 w-3.5" /></button>
        <button data-testid="org-zoom-fit" onClick={fit} className="p-1.5 hover:bg-slate-50 text-slate-600" title="Fit to view"><Scan className="h-3.5 w-3.5" /></button>
      </div>

      <div className="absolute bottom-3 left-3 bg-white/95 border border-border px-3 py-2 z-10 flex flex-wrap gap-x-4 gap-y-1 max-w-md">
        {Object.entries(DEPT_COLORS).map(([d, c]) => (
          <span key={d} className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-slate-500">
            <span className="h-2 w-2 inline-block" style={{ background: c }} /> {d}
          </span>
        ))}
      </div>
    </div>
  );
}
